import { createFileRoute, Link } from "@tanstack/react-router";
import { DomainList, DomainListItemCard } from "@/components/domain";
import { T } from "@/i18n";
import { BookOpen, EuroIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetPaymentAdjustments,
  useDeletePaymentAdjustment,
} from "@/hooks/payment-adjustment";
import { PaymentAdjustment } from "@/models/payment-adjustment";
import { Client, Reservation } from "@/models";
import { useTranslation } from "react-i18next";
import { useGetReservation } from "@/hooks/reservations";
import { useGetClients } from "@/hooks/clients";
export const Route = createFileRoute(
  "/(payment-adjustments)/payment-adjustments/$id",
)({
  component: RouteComponent,
});

export function RouteComponent() {
  const { id } = Route.useParams();
  //const navigate = Route.useNavigate();
  const { isLoading, data: reservationData } = useGetReservation(id);

  const { isLoading: isLoadingPA, data } = useGetPaymentAdjustments();
  const { isLoading: clientsLoading, data: clientsData } = useGetClients();
  return (
    <DomainList
      domain="paymentAdjustments"
      breadcrumbs={[
        {
          content: (
            <>
              <T tKey="reservations.title" />{" "}
              <BookOpen width={16} height={16} />
            </>
          ),
          href: "/reservations",
        },
        {
          content:
            isLoading || !data ? (
              <Skeleton className="h-5 w-10" />
            ) : (
              <>
                {reservationData?.id}
                <BookOpen width={16} height={16} />
              </>
            ),
          href: "/reservations/$id",
          params: { id },
        },
        {
          content: (
            <>
              <T tKey="paymentAdjustments.title" />{" "}
              <EuroIcon width={16} height={16} />
            </>
          ),
          href: "/reservations/$id",
          params: { id },
        },
      ]}
      tableOptions={{
        hasActions: false,
        columns: [
          {
            header: "paymentAdjustments.fields.type",
          },
          {
            header: "paymentAdjustments.fields.pvp",
          },
          {
            header: "paymentAdjustments.fields.comissions",
          },
          {
            header: "paymentAdjustments.fields.client",
          },
          {
            header: "paymentAdjustments.fields.markup",
          },
          {
            header: "paymentAdjustments.fields.discount",
          },
          {
            header: "paymentAdjustments.fields.total",
          },
        ],
      }}
      type="table"
      data={data}
      isLoading={isLoadingPA || clientsLoading}
      newLink="/payment-adjustments/$id/new"
      newLinkParams={{ id }}
      renderItem={(item, index, isTable) => (
        <AdjustmentRow
          isTable={isTable}
          reservation={reservationData!}
          paymentAdjustment={item}
          key={index}
          reservationClients={clientsData!}
        />
      )}
    />
  );
}

function AdjustmentRow({
  paymentAdjustment,
  isTable,
  reservation,
  reservationClients,
}: {
  paymentAdjustment: PaymentAdjustment;
  isTable: boolean;
  reservation: Reservation;
  reservationClients: Client[];
}) {
  const { mutateAsync } = useDeletePaymentAdjustment();
  const deletePaymentAdjustment = () =>
    mutateAsync(paymentAdjustment.id.toString());
  const { t } = useTranslation();

  return (
    <DomainListItemCard
      table={isTable}
      domain="paymentAdjustments"
      title={paymentAdjustment.type || "-"}
      titleTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/payment-adjustments/$id/$pid"
          params={{ id: reservation.id, pid: paymentAdjustment.id }}
        >
          {children}
        </Link>
      )}
      contentMap={[
        {
          label: t("paymentAdjustments.fields.pvp"),
          value: paymentAdjustment.pvp ?? "-",
        },
        {
          label: t("paymentAdjustments.fields.comissions"),
          value: paymentAdjustment.comissions ?? "-",
        },
        {
          label: t("paymentAdjustments.fields.client"),
          value:
            (paymentAdjustment?.paymentAdjustmentReservationClients?.length ??
              0) > 0
              ? (reservationClients?.find(
                  (c) =>
                    c.id ===
                    paymentAdjustment?.paymentAdjustmentReservationClients?.[0]
                      ?.clientId,
                )?.name ?? "-")
              : "-",
        },
        {
          label: t("paymentAdjustments.fields.markup"),
          value: paymentAdjustment?.markup ?? "-",
        },
        {
          label: t("paymentAdjustments.fields.discount"),
          value: paymentAdjustment.discount ?? "-",
        },
        {
          label: t("paymentAdjustments.fields.total"),
          value: paymentAdjustment.total ?? "-",
        },
      ]}
      onDelete={deletePaymentAdjustment}
    />
  );
}
